import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, Grid } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { StyledTextField } from "components/common/style-components/TextField/MuiTextField.component";
import { City, Country, State } from "country-state-city";
import { toString } from "lodash";
import React, { useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { signupForm } from "utils/forms-constants/forms-constants.util";

const CompanyInfoStep = () => {
  const [selectedCountry, setSelectedCountry] = useState({});
  const [selectedState, setSelectedState] = useState({});

  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setConfirmShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useFormContext();

  const countries = Country?.getAllCountries().map((country) => ({
    label: country.name,
    displayValue: country.name,
    ...country,
  }));

  const states = useMemo(
    () =>
      State?.getStatesOfCountry(selectedCountry?.isoCode).map((state) => ({
        label: state.name,
        displayValue: state.name,
        ...state,
      })),
    [selectedCountry, selectedState]
  );

  const cities = useMemo(
    () =>
      City?.getCitiesOfState(
        selectedState?.countryCode,
        selectedState?.isoCode
      ).map((state) => ({
        label: state.name,
        displayValue: state.name,
        ...state,
      })),
    [selectedState]
  );

  return (
    <>
      <Grid
        item
        xs={12}
        md={6}
        justifyContent="center"
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.company_name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              required
              sx={{ width: "100%" }}
              id={signupForm.company_name}
              data-testid={signupForm.company_name}
              label="Company name"
              value={value}
              onChange={onChange}
              error={!!errors[signupForm.company_name]?.message}
              helperText={toString(errors[signupForm.company_name]?.message)}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.country}
          control={control}
          render={({ field: { onChange, ...props } }) => (
            <Autocomplete
              required
              sx={{ width: "100%" }}
              id="country-select"
              options={countries}
              autoHighlight
              onChange={(e, value) => {
                setSelectedCountry(value);
                setValue(signupForm.country, value);
                setValue(signupForm.state, []);
                setValue(signupForm.city, []);
              }}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) => {
                return option.label === value.label;
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  {option.label}
                </Box>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="Choose a country"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password", // disable autocomplete and autofill
                  }}
                  error={!!errors[signupForm.country]?.message}
                  helperText={
                    errors[signupForm.country]?.message
                      ? "Country is required"
                      : ""
                  }
                />
              )}
              {...props}
              noOptionsText="No Countries"
            />
          )}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.state}
          control={control}
          render={({ field: { onChange, ...props } }) => (
            <>
              <Autocomplete
                noOptionsText={`No states ${
                  selectedCountry?.label ? "for " + selectedCountry?.label : ""
                }`}
                sx={{ width: "100%" }}
                id="state-select"
                options={states}
                {...props}
                getOptionLabel={(option) => option.label || ""}
                autoHighlight
                onChange={(e, value) => {
                  setSelectedState(value);

                  setValue(signupForm.state, value);
                  setValue(signupForm.city, []);
                }}
                isOptionEqualToValue={(option, value) => {
                  return option.label === value.label;
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    label="Choose a state"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password", // disable autocomplete and autofill
                    }}
                  />
                )}
              />
            </>
          )}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          style={{
            margin: 0,
          }}
          name={signupForm.city}
          control={control}
          render={({ field: { onChange, ...props } }) => (
            <Autocomplete
              noOptionsText={`No cities ${
                selectedState?.label ? "for " + selectedState?.label : ""
              }`}
              sx={{ width: "100%" }}
              id="city-select"
              options={cities}
              {...props}
              getOptionLabel={(option) => option.label || ""}
              onChange={(e, value) => {
                onChange(value);
                setValue(signupForm.city, value);
              }}
              isOptionEqualToValue={(option, value) => {
                return option.label === value.label;
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  {option.label}
                </Box>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="Choose a city"
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password", // disable autocomplete and autofill
                  }}
                />
              )}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        justifyContent="center"
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.street}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              sx={{ width: "100%" }}
              id={signupForm.street}
              data-testid={signupForm.street}
              label="Street"
              value={value}
              onChange={onChange}
              error={!!errors[signupForm.street]?.message}
              helperText={toString(errors[signupForm.street]?.message)}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        justifyContent="center"
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.pincode}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              required
              sx={{ width: "100%" }}
              id={signupForm.pincode}
              data-testid={signupForm.pincode}
              label="Pincode"
              value={value}
              onChange={onChange}
              error={!!errors[signupForm.pincode]?.message}
              helperText={toString(errors[signupForm.pincode]?.message)}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.password}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              required
              sx={{ width: "100%" }}
              id={signupForm.password}
              data-testid={signupForm.password}
              label="Password"
              value={value}
              onChange={onChange}
              InputProps={{
                type: showPassword ? "text" : "password",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors[signupForm.password]?.message}
              helperText={toString(errors[signupForm.password]?.message)}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Controller
          name={signupForm.confirm_password}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledTextField
              required
              sx={{ width: "100%" }}
              id={signupForm.confirm_password}
              data-testid={signupForm.confirm_password}
              label="Confirm Password"
              value={value}
              onChange={onChange}
              InputProps={{
                type: confirmShowPassword ? "text" : "password",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {confirmShowPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors[signupForm.confirm_password]?.message}
              helperText={toString(
                errors[signupForm.confirm_password]?.message
              )}
            />
          )}
        />
      </Grid>
    </>
  );
};

export default CompanyInfoStep;
